import React, { useMemo, useRef } from "react";
import { Checkbox, Radio, TableColumnsType, TableProps } from "antd";
import { TableRowSelection } from "antd/es/table/interface";
import {
  VirtualExpandTable,
  VirtualTable,
} from "components/atoms/table/AntdVirtualTable";
import type { Reference } from "rc-table";
import styled from "styled-components";
import * as mediaQuery from "components/MediaQuery";
import { css } from "styled-components";

export type SearchVirtualTableRecord = {
  key: React.Key;
  subRows?: unknown[];
};

const SELECTION_SELECT_ALL_TEST_ID = "search-virtual-table-checkbox-select-all";

const selectionTestId = (index: number, isRadio: boolean) =>
  isRadio
    ? `search-virtual-table-radio-${index}`
    : `search-virtual-table-checkbox-${index}`;

const withSelectAllTestId = (
  checkboxNode: React.ReactNode
): React.ReactNode => {
  if (!React.isValidElement(checkboxNode)) {
    return checkboxNode;
  }
  return React.cloneElement(checkboxNode, {
    "data-testid": SELECTION_SELECT_ALL_TEST_ID,
  } as React.HTMLAttributes<HTMLElement>);
};

const wrapSelectionColumnTitle = <T,>(
  customColumnTitle?: TableRowSelection<T>["columnTitle"]
): TableRowSelection<T>["columnTitle"] => {
  if (typeof customColumnTitle === "function") {
    return (checkboxNode) =>
      customColumnTitle(withSelectAllTestId(checkboxNode));
  }
  if (customColumnTitle !== undefined) {
    return customColumnTitle;
  }
  return withSelectAllTestId;
};

const withSelectionTestId = (
  originNode: React.ReactNode,
  index: number,
  isRadio: boolean
): React.ReactNode => {
  if (!React.isValidElement(originNode)) {
    return originNode;
  }
  return React.cloneElement(originNode, {
    "data-testid": selectionTestId(index, isRadio),
  } as React.HTMLAttributes<HTMLElement>);
};

const wrapSelectionRenderCell = <T,>(
  isRadio: boolean,
  shouldShow?: (record: T) => boolean,
  customRenderCell?: TableRowSelection<T>["renderCell"]
): NonNullable<TableRowSelection<T>["renderCell"]> => {
  return (checked, record, index, originNode) => {
    if (shouldShow && !shouldShow(record)) {
      return null;
    }
    const nodeWithTestId = withSelectionTestId(originNode, index, isRadio);
    return customRenderCell
      ? customRenderCell(checked, record, index, nodeWithTestId)
      : nodeWithTestId;
  };
};

export type SearchVirtualTableProps<T extends SearchVirtualTableRecord> = {
  columns: TableColumnsType<T>;
  dataSource: T[];
  loading?: boolean;
  isSingleMode?: boolean;
  rowExpandable?: (record: T) => boolean;
  expandedRowClassName?: (record: T) => string;
  selectedRowKeys: React.Key[];
  onSelectChange?: (selectedRowKeys: React.Key[], selectedRows?: T[]) => void;
  rowSelectable?: (record: T) => boolean;
  useExpandable?: boolean;
  rowExpandedSelection?: TableRowSelection<T>;
  expandedRowRender?: (subRows: T[]) => React.ReactNode;
  useRowExpandedSelection?: boolean;
  checkDisabled?: (record: T) => boolean;
};

export function SearchVirtualTable<T extends SearchVirtualTableRecord>(
  props: SearchVirtualTableProps<T>
): JSX.Element {
  const {
    columns,
    dataSource,
    loading = false,
    isSingleMode = false,
    expandedRowClassName,
    selectedRowKeys,
    onSelectChange,
    rowExpandable = (record: T) => Boolean(record.subRows?.length),
    rowSelectable,
    useExpandable = true,
    useRowExpandedSelection = false,
  } = props;

  const tableRef = useRef<Reference>(null);
  const subRowsRef = useRef<T[]>([]);

  const rowExpandableFn = useMemo(
    () =>
      props.rowExpandable ?? ((record: T) => Boolean(record.subRows?.length)),
    [props.rowExpandable]
  );
  const rowExpandedSelection = useMemo((): TableRowSelection<T> | undefined => {
    if (!useRowExpandedSelection) {
      return undefined;
    }
    if (props.rowExpandedSelection) {
      const {
        type,
        renderCell: customRenderCell,
        columnTitle: customColumnTitle,
        ...rest
      } = props.rowExpandedSelection;
      const isRadio = type === "radio";
      return {
        ...rest,
        type,
        renderCell: wrapSelectionRenderCell(
          isRadio,
          undefined,
          customRenderCell
        ),
        ...(!isRadio && {
          columnTitle: wrapSelectionColumnTitle(customColumnTitle),
        }),
      };
    }
    return {
      columnWidth: 30,
      selectedRowKeys,
      onChange: (keys: React.Key[], rows: T[]) => onSelectChange?.(keys, rows),
      type: isSingleMode ? "radio" : "checkbox",
      preserveSelectedRowKeys: true,
      renderCell: wrapSelectionRenderCell(isSingleMode),
      ...(!isSingleMode && {
        columnTitle: wrapSelectionColumnTitle(),
      }),
      getCheckboxProps: (record: T) => ({
        disabled: props.checkDisabled?.(record) ?? false,
      }),
    };
  }, [
    isSingleMode,
    onSelectChange,
    props,
    selectedRowKeys,
    useRowExpandedSelection,
  ]);
  const rowSelectableFn = useMemo(
    () => rowSelectable ?? ((record: T) => true),
    [rowSelectable]
  );
  const rowSelection: TableRowSelection<T> = {
    columnWidth: 30,
    selectedRowKeys,
    onChange: onSelectChange,
    preserveSelectedRowKeys: true,
    type: props.isSingleMode ? "radio" : "checkbox",
    renderCell: wrapSelectionRenderCell(
      props.isSingleMode ?? false,
      (record) => rowExpandableFn(record) || rowSelectableFn(record)
    ),
    ...(!props.isSingleMode && {
      columnTitle: wrapSelectionColumnTitle(),
    }),
    getCheckboxProps: (record: T) => ({
      disabled: props.checkDisabled?.(record) ?? false,
    }),
  };
  const expandColumns: TableColumnsType<T> = useMemo(() => {
    const placeholders = [
      { width: 30, key: "expand-placeholder-1", render: () => null },
      { width: 30, key: "expand-placeholder-2", render: () => null },
    ];
    if (isSingleMode) {
      return [
        { width: 30, hidden: true, key: "ph1" },
        { width: 30, hidden: true, key: "ph2" },
        ...columns,
      ];
    }
    if (!rowExpandedSelection) {
      return [...placeholders, ...columns];
    }
    const { selectedRowKeys, onChange, getCheckboxProps, type } =
      rowExpandedSelection;
    const isRadio = type === "radio";
    const selectionColumn = {
      width: rowExpandedSelection.columnWidth ?? 30,
      key: "expand-selection",
      className: "ant-table-selection-column",
      align: "center" as const,
      render: (_: unknown, record: T, index: number) => {
        const props = getCheckboxProps?.(record) ?? {};
        const checked = selectedRowKeys?.includes(record.key) ?? false;
        const disabled = props.disabled ?? false;
        const handleChange = () => {
          if (isRadio) {
            onChange?.([record.key], [record], { type: "single" });
          } else {
            const current = selectedRowKeys ?? [];
            const next = current.includes(record.key)
              ? current.filter((k) => k !== record.key)
              : [...current, record.key];
            const nextRows = subRowsRef.current?.filter((r) =>
              next.includes(r.key)
            ) as T[];
            onChange?.(next, nextRows ?? [], { type: "multiple" });
          }
        };
        return isRadio ? (
          <Radio
            checked={checked}
            disabled={disabled}
            onChange={handleChange}
            data-testid={`search-virtual-table-radio-${index}`}
          />
        ) : (
          <Checkbox
            checked={checked}
            disabled={disabled}
            onChange={handleChange}
            data-testid={`search-virtual-table-checkbox-${index}`}
          />
        );
      },
    };
    return [...placeholders, selectionColumn, ...columns];
  }, [columns, isSingleMode, rowExpandedSelection]);

  const expandedRowRender = useMemo(
    () => (subRows: T[]) => {
      subRowsRef.current = subRows;
      if (props.expandedRowRender) {
        return props.expandedRowRender(subRows);
      }
      return (
        <VirtualExpandTable
          columns={expandColumns}
          dataSource={subRows}
          showHeader={false}
          rowSelection={
            useRowExpandedSelection ? rowExpandedSelection : undefined
          }
        />
      );
    },
    [props, expandColumns, useRowExpandedSelection, rowExpandedSelection]
  );

  const expandableProps = useMemo<TableProps<T>["expandable"]>(() => {
    if (!useExpandable) {
      return undefined;
    }
    return {
      columnWidth: 30,
      expandedRowRender: (record: T) =>
        record.subRows?.length
          ? expandedRowRender(record.subRows as T[])
          : undefined,
      rowExpandable,
      expandedRowClassName: (record: T) =>
        record.subRows?.length
          ? expandedRowClassName?.(record) ?? "expander-children-row"
          : "",
      onExpand: (expanded: boolean, record: T) => {
        if (expanded && tableRef?.current) {
          const currentIndex = dataSource.findIndex(
            (row) =>
              (row as { key: React.Key }).key ===
              (record as { key: React.Key }).key
          );
          if (currentIndex > 0) {
            requestAnimationFrame(() => {
              (tableRef as React.RefObject<Reference>)?.current?.scrollTo({
                index: currentIndex,
              });
            });
          }
        }
      },
    };
  }, [
    expandedRowRender,
    rowExpandable,
    expandedRowClassName,
    dataSource,
    tableRef,
  ]);

  const rowClassName = useMemo(
    () => (record: T) =>
      rowExpandable(record)
        ? `${isSingleMode ? "single" : "multi"}-expander-row`
        : "",
    [isSingleMode, rowExpandable]
  );

  return (
    <SearchRecorderWrapper isSingleMode={isSingleMode}>
      <VirtualTable
        ref={tableRef}
        columns={columns}
        dataSource={dataSource}
        rowSelection={onSelectChange && rowSelection ? rowSelection : undefined}
        loading={loading}
        expandable={
          onSelectChange && rowSelection ? expandableProps : undefined
        }
        rowClassName={rowClassName}
      />
    </SearchRecorderWrapper>
  );
}

export const SearchRecorderWrapper = styled.div<{ isSingleMode?: boolean }>`
  .virtual-expand-table {
    padding-right: 0 !important;
  }
  .ant-table-row-expand-icon-cell {
    button {
      width: 100%;
    }
  }
  ${mediaQuery.isMobile &&
  css`
    .ant-table-row .location {
      opacity: 0.5;
    }
  `}
  ${(props) =>
    mediaQuery.isMobile && props.isSingleMode
      ? css`
          .ant-table-row:not(.single-expander-row) {
            .ant-table-row-expand-icon-cell {
              display: none;
            }
          }
          .virtual-expand-table td.ant-table-selection-column {
            text-align: left;
            padding-left: 44px !important;
          }
          .ant-table-selection-column .ant-radio-wrapper {
            vertical-align: middle;
            &:after {
              display: none;
            }
          }
          .ant-table-selection-column + div {
            flex: 1 !important;
            width: auto !important;
          }
          .single-expander-row .ant-table-row-expand-icon-cell + div {
            display: none;
          }
        `
      : css`
          .virtual-expand-table {
            td:not(.ant-table-selection-column) + td.name {
              text-align: left;
              padding-left: 25px !important;
            }
          }
        `}
`;
